import React from 'react';
import { toast } from 'react-toastify';
import { getFileAssetBlob, uploadFileAsset } from '../../../../../helpers/file-helper';
import { createTicketFollow, updateTicket } from '../../../../../helpers/ticket-helper';
import { getPlainTextFromHtml, parseCustomFieldValue } from '../helpers';

export const useTaskDetailActions = ({
    token,
    ticketId,
    ticket,
    form,
    tagBadges,
    additionalFieldDefs,
    additionalFieldValues,
    additionalTabs,
    setActiveMainTab,
    loadTicket,
    followEditorRef,
    followFilesInputRef,
    followSortMenuRef,
    ticketSignature,
}) => {
    const [isSaving, setIsSaving] = React.useState(false);
    const [isSavingFollow, setIsSavingFollow] = React.useState(false);
    const [isResolving, setIsResolving] = React.useState(false);

    const [followHtml, setFollowHtml] = React.useState('');
    const [followIsPublic, setFollowIsPublic] = React.useState(true);
    const [followAttachments, setFollowAttachments] = React.useState([]);
    const [composerMode, setComposerMode] = React.useState('follow');
    const [openAttachmentMenuId, setOpenAttachmentMenuId] = React.useState('');
    const [followSortOrder, setFollowSortOrder] = React.useState('DESC');
    const [activeFollowTab, setActiveFollowTab] = React.useState('all');
    const [isFollowSortMenuOpen, setIsFollowSortMenuOpen] = React.useState(false);

    React.useEffect(() => {
        if (!openAttachmentMenuId) return undefined;

        const handleOutsideClick = (event) => {
            if (event?.target instanceof Element && event.target.closest('[data-attachment-menu-root]')) {
                return;
            }
            setOpenAttachmentMenuId('');
        };

        document.addEventListener('mousedown', handleOutsideClick);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, [openAttachmentMenuId]);

    React.useEffect(() => {
        if (!isFollowSortMenuOpen) return undefined;

        const handleSortMenuOutsideClick = (event) => {
            if (!followSortMenuRef.current?.contains(event.target)) {
                setIsFollowSortMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleSortMenuOutsideClick);
        return () => {
            document.removeEventListener('mousedown', handleSortMenuOutsideClick);
        };
    }, [isFollowSortMenuOpen, followSortMenuRef]);

    const handleSubmit = async (event) => {
        event?.preventDefault?.();

        if (!token || !ticketId || isSaving) return;

        setIsSaving(true);

        const payload = {
            title: String(form.title || '').trim(),
            description: String(form.description || '').trim(),
            type: form.type || undefined,
            source: form.source || undefined,
            requester: form.requester || undefined,
            category: form.category || undefined,
            subcategory: form.subcategory || undefined,
            tags: tagBadges,
            status: form.status,
            priority: form.priority,
            impact: form.impact,
            urgency: form.urgency,
            assignee: form.assignee || undefined,
            group: form.group || '',
            customFields: {},
        };

        for (let index = 0; index < additionalFieldDefs.length; index += 1) {
            const field = additionalFieldDefs[index];
            const key = field.key || field._id;
            if (!key) {
                continue;
            }

            const mappedValue = parseCustomFieldValue(field, additionalFieldValues[key]);

            if (field.required && (mappedValue === undefined || mappedValue === '')) {
                toast.error(`Completa el campo adicional requerido: ${field.name || key}.`);
                const targetTab = additionalTabs.find((tab) => tab.fields.some((tabField) => String(tabField.key) === String(key)));
                if (targetTab?.key) {
                    setActiveMainTab(`additional-${targetTab.key}`);
                }
                setIsSaving(false);
                return;
            }

            if (mappedValue !== undefined) {
                payload.customFields[key] = mappedValue;
            }
        }

        if (Object.keys(payload.customFields).length === 0) {
            delete payload.customFields;
        }

        if (!payload.title || !payload.description) {
            toast.error('Título y descripción son obligatorios para guardar cambios.');
            setIsSaving(false);
            return;
        }

        const resp = await updateTicket(token, ticketId, payload);

        if (!resp?.success) {
            toast.error(resp?.message || 'No se pudo actualizar el ticket.');
            setIsSaving(false);
            return;
        }

        toast.success(resp?.message || 'Ticket actualizado correctamente.');
        setIsSaving(false);
        await loadTicket();
    };

    const handleStartResolutionMode = () => {
        setComposerMode('resolution');
        setActiveMainTab('content');

        if (followEditorRef.current) {
            setTimeout(() => {
                followEditorRef.current?.focus();
            }, 0);
        }
    };

    const handleDownloadAttachment = async (file) => {
        if (!file?._id || !token) return;

        setOpenAttachmentMenuId('');

        const resp = await getFileAssetBlob(token, file._id);
        if (!resp?.success || !resp?.data) {
            toast.error(resp?.message || 'No se pudo descargar el adjunto.');
            return;
        }

        const blob = new Blob([resp.data], { type: file.mimeType || 'application/octet-stream' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${file.originalName || file.fileName || 'adjunto'}.${file.extension || ''}`.replace(/\.$/, '');
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    };

    const handleFollowEditorInput = (event) => {
        setFollowHtml(event.currentTarget?.innerHTML || '');
    };

    const handleFollowAttachmentsChange = (event) => {
        setFollowAttachments(Array.from(event.target.files || []));
    };

    const handleOpenFollowFilesPicker = React.useCallback(() => {
        followFilesInputRef.current?.click();
    }, [followFilesInputRef]);

    const handleSelectFollowSortOrder = React.useCallback((nextOrder) => {
        setFollowSortOrder(nextOrder);
        setIsFollowSortMenuOpen(false);
    }, []);

    const handleToggleAttachmentMenu = (fileId) => {
        if (!fileId) return;
        setOpenAttachmentMenuId((previous) => (previous === fileId ? '' : fileId));
    };

    const buildFollowHtmlWithSignature = React.useCallback((rawHtml = '') => {
        const baseHtml = String(rawHtml || '').trim();
        const signatureHtml = String(ticketSignature || '').trim();

        if (!signatureHtml) {
            return baseHtml;
        }

        if (!baseHtml) {
            return signatureHtml;
        }

        const normalizedBase = baseHtml.replace(/\s+/g, ' ').trim();
        const normalizedSignature = signatureHtml.replace(/\s+/g, ' ').trim();

        if (normalizedSignature && normalizedBase.includes(normalizedSignature)) {
            return baseHtml;
        }

        return `${baseHtml}<br/><br/>${signatureHtml}`;
    }, [ticketSignature]);

    const handleCreateFollow = async () => {
        if (!token || !ticketId || isSavingFollow || isResolving) return;

        if (!getPlainTextFromHtml(followHtml)) {
            toast.error(composerMode === 'resolution'
                ? 'Escribe el contenido de la resolución.'
                : 'Escribe contenido para el seguimiento.');
            return;
        }

        if (composerMode === 'resolution') {
            setIsResolving(true);
        } else {
            setIsSavingFollow(true);
        }

        const contentHtmlWithSignature = buildFollowHtmlWithSignature(followHtml);

        const followResp = await createTicketFollow(token, ticketId, {
            contentHtml: contentHtmlWithSignature,
            isPublic: followIsPublic,
        });

        if (!followResp?.success || !followResp?.data) {
            toast.error(followResp?.message || 'No se pudo guardar la actividad.');
            setIsSavingFollow(false);
            setIsResolving(false);
            return;
        }

        if (composerMode === 'resolution') {
            const resolveResp = await updateTicket(token, ticketId, {
                status: 'RESOLVED',
                resolutionSummary: String(followHtml || '').trim(),
                resolvedAt: new Date().toISOString(),
                title: String(form.title || '').trim(),
                description: String(form.description || '').trim(),
                type: form.type || undefined,
                source: form.source || undefined,
                requester: form.requester || undefined,
                category: form.category || undefined,
                subcategory: form.subcategory || undefined,
                tags: tagBadges,
                assignee: form.assignee || undefined,
                group: form.group || '',
                priority: form.priority,
                impact: form.impact,
                urgency: form.urgency,
            });

            if (!resolveResp?.success) {
                toast.error(resolveResp?.message || 'No se pudo resolver el ticket.');
                setIsSavingFollow(false);
                setIsResolving(false);
                return;
            }

            if (Array.isArray(followAttachments) && followAttachments.length > 0) {
                const uploadResponses = await Promise.all(followAttachments.map((file) => {
                    return uploadFileAsset(token, {
                        file,
                        purpose: 'TICKET_RESOLUTION_ATTACHMENT',
                        tags: ['ticket-resolution', ticketId],
                        referenceType: 'TICKET',
                        referenceId: ticketId,
                        referenceField: 'resolution',
                        referenceLabel: ticket?.code || ticketId,
                        isPublic: false,
                    });
                }));

                const failedUpload = uploadResponses.find((response) => !response?.success);
                if (failedUpload) {
                    toast.error(failedUpload?.message || 'No se pudieron subir uno o más adjuntos de resolución.');
                }
            }
        } else if (Array.isArray(followAttachments) && followAttachments.length > 0) {
            const uploadResponses = await Promise.all(followAttachments.map((file) => {
                return uploadFileAsset(token, {
                    file,
                    purpose: 'TICKET_FOLLOW_ATTACHMENT',
                    tags: ['ticket-follow', ticketId],
                    referenceType: 'TICKET_FOLLOW',
                    referenceId: followResp.data._id,
                    referenceField: 'attachments',
                    referenceLabel: ticket?.code || ticketId,
                    isPublic: false,
                });
            }));

            const failedUpload = uploadResponses.find((response) => !response?.success);
            if (failedUpload) {
                toast.error(failedUpload?.message || 'No se pudieron subir uno o más adjuntos del seguimiento.');
            }
        }

        setFollowHtml('');
        setFollowAttachments([]);
        setComposerMode('follow');
        if (followEditorRef.current) {
            followEditorRef.current.innerHTML = '';
        }
        if (followFilesInputRef.current) {
            followFilesInputRef.current.value = '';
        }

        toast.success(composerMode === 'resolution'
            ? 'Ticket resuelto exitosamente.'
            : (followResp?.message || 'Seguimiento creado exitosamente.'));
        setIsSavingFollow(false);
        setIsResolving(false);
        await loadTicket();
    };

    return {
        isSaving,
        isSavingFollow,
        isResolving,
        followHtml,
        followIsPublic,
        followAttachments,
        composerMode,
        openAttachmentMenuId,
        followSortOrder,
        activeFollowTab,
        isFollowSortMenuOpen,
        setFollowHtml,
        setFollowIsPublic,
        setComposerMode,
        setActiveFollowTab,
        setIsFollowSortMenuOpen,
        handleSubmit,
        handleStartResolutionMode,
        handleDownloadAttachment,
        handleFollowEditorInput,
        handleFollowAttachmentsChange,
        handleOpenFollowFilesPicker,
        handleSelectFollowSortOrder,
        handleToggleAttachmentMenu,
        handleCreateFollow,
    };
};